// Viewport service (IMPLEMENTATION §2.6, §2.9): visualViewport size, safe-area insets via a probe element,
// effective DPR = min(dpr, cap). Recomputes on visualViewport resize / orientation change, debounced.
import type { Insets } from '../layout/computeLayout.ts';

export interface ViewportInfo {
  /** Visible viewport (visualViewport) in CSS px: feeds computeLayout. */
  vw: number;
  vh: number;
  /** Layout viewport (innerWidth/innerHeight) in CSS px: the full-screen canvas size. */
  iw: number;
  ih: number;
  insets: Insets;
  dpr: number;
  /** min(dpr, cap): the canvas backing-store scale. */
  dprEff: number;
}

/** Effective DPR (§2.9): min(dpr, cap). Pure. */
export function effectiveDpr(dpr: number, cap: number): number {
  const d = Number.isFinite(dpr) && dpr > 0 ? dpr : 1;
  return Math.min(d, cap);
}

/** A hidden fixed element padded by env(safe-area-inset-*); its computed padding is the inset in px. */
export function createSafeAreaProbe(doc: Document = document): () => Insets {
  const el = doc.createElement('div');
  el.setAttribute('aria-hidden', 'true');
  el.style.cssText =
    'position:fixed;left:0;top:0;width:0;height:0;visibility:hidden;pointer-events:none;' +
    'padding:env(safe-area-inset-top,0px) env(safe-area-inset-right,0px) env(safe-area-inset-bottom,0px) env(safe-area-inset-left,0px);';
  doc.body.appendChild(el);
  return () => {
    const cs = getComputedStyle(el);
    const px = (v: string): number => {
      const n = Number.parseFloat(v);
      return Number.isFinite(n) ? n : 0;
    };
    return {
      top: px(cs.paddingTop),
      right: px(cs.paddingRight),
      bottom: px(cs.paddingBottom),
      left: px(cs.paddingLeft),
    };
  };
}

export function readViewport(
  probe: () => Insets,
  dprCap: number,
  win: Window = window,
): ViewportInfo {
  const vv = win.visualViewport;
  const iw = win.innerWidth;
  const ih = win.innerHeight;
  const dpr = win.devicePixelRatio || 1;
  return {
    vw: vv ? vv.width : iw,
    vh: vv ? vv.height : ih,
    iw,
    ih,
    insets: probe(),
    dpr,
    dprEff: effectiveDpr(dpr, dprCap),
  };
}

/** Calls `cb` (debounced) on visualViewport resize, window resize and orientation change. Returns an unsubscribe. */
export function watchViewport(
  cb: () => void,
  debounceMs: number,
  win: Window = window,
): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const fire = (): void => {
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      cb();
    }, debounceMs);
  };
  const vv = win.visualViewport;
  vv?.addEventListener('resize', fire);
  win.addEventListener('resize', fire);
  win.addEventListener('orientationchange', fire);
  return () => {
    if (timer !== null) clearTimeout(timer);
    vv?.removeEventListener('resize', fire);
    win.removeEventListener('resize', fire);
    win.removeEventListener('orientationchange', fire);
  };
}
